import boatDemo from "./examples/boat-demo.json";
import { endpointPos, shrinkBusbarsToCableCount } from "./store-helpers";
import { tidySystemCables } from "./cable-tidy";
import { migrate } from "./schema/migrate";
import type { System } from "./types";
import { computeVdropAllowances, VDROP_PCT_NON_CRITICAL } from "./vdrop-allowance";

/** Keep the larger example readable as functional groups with open routing
 * corridors, rather than preserving the exported editor's crowded positions. */
function layoutBoat(system: System): void {
  const zones: Record<string, [number, number, number, number]> = {
    LN_8pwap: [0, 240, 1210, 720], j7T6biJY: [1300, 240, 600, 580],
    "68Zlo47F": [1480, 20, 400, 180], lDQ2niwD: [1980, 20, 620, 840],
    d9itlM4r: [1300, 980, 1300, 550], "9bpe07-W": [0, -520, 600, 220],
    HIWrBiqJ: [630, -480, 500, 240],
  };
  const positions: Record<string, [number, number]> = {
    J0cGPCqW: [40, 320], BAdWfQr1: [40, 620], CWcLCA_g: [360, 320],
    mue2KX26: [700, 320], "1JNdXYFb": [700, 580], qll9YJmT: [580, 540],
    "U-7UN09i": [1060, 320], cpWzpubx: [1060, 620], "3IIqkt8R": [1240, 335],
    x1TDnOgI: [700, 800], UBo1lmJN: [1380, 320], vUjoM2rU: [1800, 620],
    "20AbEWRO": [1550, 60], ZSspTmEc: [1550, 480], "9ms-jnC7": [1100, 820],
    muxZuLVE: [40, -460], "7QOnNEuE": [320, -460], FqNXhz5e: [560, -440], lxhoJmSp: [560, -340],
    "boat-pv-isolator": [680, -400], "wUAorg5-": [900, -410],
    iyyn4AZW: [2020, 300], TAyiey2o: [2510, 300],
    W5gHEGR0: [2220, 60], GGQ4GAzf: [2220, 210], uSm29TMY: [2220, 360], LBc2SRSt: [2220, 510], "NkgNA-RV": [2220, 660],
    mWSL8V8L: [1420, 1040], Dw5tsBwv: [2440, 1360],
    "7rad3fN4": [1480, 1190], mYbv5Suz: [1740, 1190], uemz1Bsi: [2000, 1190], qda5KuXM: [2260, 1190],
  };
  const pair = { pos: { side: "left" as const, offset: 0.3, pinned: true }, neg: { side: "right" as const, offset: 0.75, pinned: true } };
  for (const item of system.nodes) {
    if (item.kind === "zone") {
      const [x, y, width, height] = zones[item.id];
      Object.assign(item, { position: { x, y }, width, height });
      continue;
    }
    const [x, y] = positions[item.id];
    const parent = item.parentId ? zones[item.parentId] : undefined;
    item.position = { x: x - (parent?.[0] ?? 0), y: y - (parent?.[1] ?? 0) };
    item.rotation = 0;
    item.terminalOverrides = undefined;
    if (["20AbEWRO", "ZSspTmEc", "W5gHEGR0", "GGQ4GAzf", "uSm29TMY", "LBc2SRSt", "NkgNA-RV"].includes(item.id)) item.terminalOverrides = pair;
    if (["vUjoM2rU", "TAyiey2o", "Dw5tsBwv"].includes(item.id)) item.rotation = 180;
    if (["7rad3fN4", "mYbv5Suz", "uemz1Bsi", "qda5KuXM"].includes(item.id)) item.terminalOverrides = { pos: { side: "top", offset: 0.3, pinned: true }, neg: { side: "bottom", offset: 0.7, pinned: true } };
    if (item.id === "J0cGPCqW") item.terminalOverrides = { pos: { side: "bottom", offset: 0.8, pinned: true }, neg: { side: "left", offset: 0.7, pinned: true } };
    if (item.id === "BAdWfQr1") item.terminalOverrides = { starter: { side: "top", offset: 0.8, pinned: true }, gnd: { side: "left", offset: 0.7, pinned: true }, "alt-pos": { side: "right", offset: 0.3, pinned: true } };
    if (item.id === "mue2KX26") item.terminalOverrides = { pos: { side: "right", offset: 0.3, pinned: true }, neg: { side: "bottom", offset: 0.5, pinned: true } };
    if (item.id === "1JNdXYFb") item.terminalOverrides = { "batt-neg": { side: "top", offset: 0.5, pinned: true }, "load-neg": { side: "right", offset: 0.7, pinned: true }, vbat: { side: "right", offset: 0.2, pinned: true } };
    if (item.id === "CWcLCA_g") item.terminalOverrides = { "in-pos": { side: "left", offset: 0.3, pinned: true }, "in-neg": { side: "bottom", offset: 0.2, pinned: true }, "out-pos": { side: "right", offset: 0.3, pinned: true }, "out-neg": { side: "bottom", offset: 0.8, pinned: true } };
    if (item.id === "wUAorg5-") item.terminalOverrides = { "pv-pos": { side: "left", offset: 0.3, pinned: true }, "pv-neg": { side: "left", offset: 0.7, pinned: true }, "batt-pos": { side: "right", offset: 0.3, pinned: true }, "batt-neg": { side: "bottom", offset: 0.5, pinned: true } };
    if (item.id === "muxZuLVE" || item.id === "7QOnNEuE") item.terminalOverrides = { pos: { side: "top", offset: 0.75, pinned: true }, neg: { side: "bottom", offset: 0.75, pinned: true } };
  }
}

// Adapt the supplied boat demo without changing the saved source fixture.
// Correct the incomplete charger return, the SmartShunt supply and PV isolation
// against the manufacturer manuals before publishing it as a starting example.
function demoSystem(focused: boolean): System {
  const result = migrate(structuredClone(boatDemo));
  if (!result.ok) throw new Error("The boat wiring demo could not be loaded");
  const system = result.system;
  // SmartShunt §3.4 and technical data §11.1: one dedicated 1 A fused sense
  // supply. A monitor sense terminal must never join charging power paths.
  system.cables = system.cables.filter((cable) => cable.id !== "wKr8SepO").map((cable) => {
    if (cable.id === "9EGLmIEh") return { ...cable, fuseAmpOverride: 1 };
    // The charger branch joins the unswitched distribution bus, downstream of
    // the battery's 70 A post fuse. Its 60 A fuse is a distinct branch device.
    if (cable.id === "dzeimzaO") return { ...cable, toNodeId: "U-7UN09i", toTerminalId: "a", fuseAmpOverride: 60, wireSizeMm2Override: 10 };
    if (cable.id === "Lzv4MgvM") return { ...cable, fuseAmpOverride: 60, wireSizeMm2Override: 10 };
    if (cable.id === "fC1BOnRG" || cable.id === "NCszJNAT") return { ...cable, wireSizeMm2Override: 10 };
    // This common negative link can carry charging current as well as loads;
    // its automatic 6 mm² choice was undersized after completing the returns.
    if (cable.id === "JZlkgFXA") return { ...cable, wireSizeMm2Override: 16 };
    // A dedicated refrigerator branch uses the CRX manual's 15 A choice.
    if (cable.id === "q-l2PGx5") return { ...cable, fuseAmpOverride: 15 };
    if (cable.id === "appi4ilZ") return { ...cable, fuseAmpOverride: 25 };
    // Keep the cabin's branch distribution together, with its own shared feed.
    if (cable.id === "3KKREuhK") return { ...cable, toNodeId: "iyyn4AZW", toTerminalId: "d" };
    if (cable.id === "VZcuFwwa") return { ...cable, toNodeId: "iyyn4AZW", toTerminalId: "e" };
    return cable;
  });
  // Complete the existing shared return junction on the system side of the
  // SmartShunt. The isolated Orion can share negatives when deliberately wired
  // that way; neither negative port may be left on a floating junction.
  system.cables.push({ id: "charger-system-return", fromNodeId: "qll9YJmT", fromTerminalId: "j", toNodeId: "1JNdXYFb", toTerminalId: "load-neg", lengthM: 0.5, wireSizeMm2Override: 16, vdropClass: "critical" });
  for (const item of system.nodes) {
    if (item.kind !== "component") continue;
    if (item.id === "CWcLCA_g") item.notes = "Orion-Tr Smart 12/12-18 isolated charger, deliberately connected to a common system negative. Both input and output returns are complete. 60 A branch fuses and 10 mm² leads follow the isolated Orion manual's 12 V / 1 m table (§4.2); installation conditions still require review. Remote/engine detection, the actual battery profile and alternator headroom are not verified. Checked 21 October 2026. https://www.victronenergy.com/media/pg/Orion-Tr_Smart_DC-DC_Charger_-_Isolated/en/installation.html";
    if (item.id === "1JNdXYFb") item.notes = "SmartShunt: only house battery negative connects to BATTERY MINUS. Loads and charger returns join SYSTEM MINUS. The unswitched positive bus supplies Vbatt+ through a dedicated 1 A fuse; it is a sense supply, never a charging junction. Victron manual §§3.3–3.4 and technical data §11.1, checked 21 October 2026.";
    if (item.id === "7QOnNEuE" || item.id === "muxZuLVE") {
      item.definitionId = "solar-renogy-100";
      delete item.customDefinition;
      item.notes = "Matched Renogy RNG-100D-SS 100 W modules replace the demo's mismatched 200 W / 100 W placeholders. Two parallel modules form a 200 W example array: 18.6 Vmp, 22.3 V STC Voc and 5.38 A Imp per module (legacy datasheet). Check actual module revision, cold Voc, Isc, connectors and string protection. https://www.renogy.com/content/RNG-100D-SS/100D-SS-Datasheet.pdf";
    }
  }
  if (!focused) {
    system.nodes.push({ id: "boat-pv-isolator", kind: "component", definitionId: "dist-solar-dp-isolator", position: { x: -150, y: -560 }, notes: "Two-pole PV isolation added following SmartSolar installation §4.3. Generic 32 A model: select an actual DC/PV-rated isolator for cold array Voc and Isc. Panel/string matching, grounding and installation rules still require review." });
    system.cables = system.cables.map((cable) => {
      if (cable.id === "kvpg0HDU") return { ...cable, toNodeId: "boat-pv-isolator", toTerminalId: "in-pos" };
      if (cable.id === "5mPpiNbb") return { ...cable, toNodeId: "boat-pv-isolator", toTerminalId: "in-neg" };
      return cable;
    });
    system.cables.push(
      { id: "boat-pv-controller-positive", fromNodeId: "boat-pv-isolator", fromTerminalId: "out-pos", toNodeId: "wUAorg5-", toTerminalId: "pv-pos", lengthM: 0.3, vdropClass: "critical" },
      { id: "boat-pv-controller-negative", fromNodeId: "boat-pv-isolator", fromTerminalId: "out-neg", toNodeId: "wUAorg5-", toTerminalId: "pv-neg", lengthM: 0.3, vdropClass: "critical" },
    );
  }
  if (focused) {
    const zones = new Set(["LN_8pwap", "j7T6biJY", "68Zlo47F"]);
    system.nodes = system.nodes.filter((node) => zones.has(node.id) || (node.parentId && zones.has(node.parentId)));
    const ids = new Set(system.nodes.map((node) => node.id));
    system.cables = system.cables.filter((cable) => ids.has(cable.fromNodeId) && ids.has(cable.toNodeId));
    system.zoneDistances = Object.fromEntries(Object.entries(system.zoneDistances).filter(([key]) => key.split("|").every((id) => ids.has(id))));
  }
  // Order each bus's outlets by the physical load rows. All are electrically
  // equivalent studs; this changes drawing order, not electrical continuity.
  const ports: Record<string, Record<string, string>> = {
    UBo1lmJN: { appi4ilZ: "b", "-8DHog3T": focused ? "b" : "c", "5ha5S7O4": focused ? "c" : "d" },
    iyyn4AZW: { noRIAsRY: "b", MD8za7NF: "c", "3KKREuhK": "d", VZcuFwwa: "e", "q-l2PGx5": "f" },
    TAyiey2o: { CdfqhK2Y: "b", "1I3VqVPU": "c", VPfdJvuE: "d", eFxqtLru: "e", v0LnyD_T: "f" },
    mWSL8V8L: { dhzJhJZp: "b", hoqONbRJ: "c", KOtOzZvx: "d", WZq27NQw: "e" },
    Dw5tsBwv: { dqPoM03D: "b", JYkHMAyf: "c", GzVpoJrp: "d", ex58zUfm: "e" },
  };
  system.cables = system.cables.map(cable => {
    const from = ports[cable.fromNodeId]?.[cable.id], to = ports[cable.toNodeId]?.[cable.id];
    return { ...cable, ...(from ? { fromTerminalId: from } : {}), ...(to ? { toTerminalId: to } : {}) };
  });
  for (const item of system.nodes) if (item.kind === "component" && ports[item.id]) {
    item.studCount = new Set(system.cables.flatMap(cable => cable.fromNodeId === item.id && cable.fromTerminalId !== "a" ? [cable.fromTerminalId] : cable.toNodeId === item.id && cable.toTerminalId !== "a" ? [cable.toTerminalId] : [])).size;
  }
  layoutBoat(system);
  const compacted = shrinkBusbarsToCableCount(system.nodes, system.cables, system.nodes.map((node) => node.id));
  system.nodes = compacted.nodes;
  system.cables = compacted.cables;
  const routes = tidySystemCables(system.nodes, system.cables.map((cable) => ({ ...cable, waypoints: undefined })), system.cables.map((cable) => cable.id));
  const setRoute = (id: string, firstNode: string, route: (from: { x: number; y: number }, to: { x: number; y: number }) => { x: number; y: number }[]) => {
    const cable = system.cables.find(item => item.id === id);
    if (!cable) return;
    const from = endpointPos(cable.fromNodeId, cable.fromTerminalId, system.nodes)!;
    const to = endpointPos(cable.toNodeId, cable.toTerminalId, system.nodes)!;
    const forwards = cable.fromNodeId === firstNode;
    const points = route(forwards ? from : to, forwards ? to : from);
    routes.set(id, forwards ? points : points.reverse());
  };
  // Separate cranking and common-return corridors below the engine group.
  setRoute("JZlkgFXA", "J0cGPCqW", (from, to) => [{ x: -80, y: from.y }, { x: -80, y: 760 }, { x: 920, y: 760 }, { x: 920, y: to.y }]);
  setRoute("VUtTeRtG", "BAdWfQr1", (from, to) => [{ x: -40, y: from.y }, { x: -40, y: to.y }]);
  setRoute("Lzv4MgvM", "BAdWfQr1", (from, to) => [{ x: 310, y: from.y }, { x: 310, y: to.y }]);
  setRoute("charger-system-return", "qll9YJmT", (from, to) => [{ x: 640, y: from.y }, { x: 640, y: 740 }, { x: 880, y: 740 }, { x: 880, y: to.y }]);
  setRoute("9EGLmIEh", "1JNdXYFb", (from, to) => [{ x: 940, y: from.y }, { x: 940, y: to.y }]);
  if (!focused) {
    setRoute("TweoenFf", "TAyiey2o", (from, to) => [{ x: 2580, y: from.y }, { x: 2580, y: 900 }, { x: 1780, y: 900 }, { x: 1780, y: to.y }]);
    setRoute("bjb1ZlZf", "Dw5tsBwv", (from, to) => [{ x: 2640, y: from.y }, { x: 2640, y: 1580 }, { x: 1340, y: 1580 }, { x: 1340, y: to.y }]);
    setRoute("x8yO2W8S", "muxZuLVE", (from, to) => [{ x: from.x, y: -560 }, { x: to.x, y: -560 }]);
    setRoute("WhOaL8AY", "7QOnNEuE", (from, to) => [{ x: from.x, y: -520 }, { x: to.x, y: -520 }]);
    for (const [id, corridor] of [["noRIAsRY", 2100], ["MD8za7NF", 2120], ["3KKREuhK", 2140], ["VZcuFwwa", 2120], ["q-l2PGx5", 2100]] as const) {
      setRoute(id, "iyyn4AZW", (from, to) => [{ x: corridor, y: from.y }, { x: corridor, y: to.y }]);
    }
    for (const [id, corridor] of [["v0LnyD_T", 2480], ["eFxqtLru", 2460], ["VPfdJvuE", 2440], ["1I3VqVPU", 2460], ["CdfqhK2Y", 2480]] as const) {
      setRoute(id, "TAyiey2o", (from, to) => [{ x: corridor, y: from.y }, { x: corridor, y: to.y }]);
    }
    for (const id of ["WZq27NQw", "KOtOzZvx", "hoqONbRJ", "dhzJhJZp"]) setRoute(id, "mWSL8V8L", (from, to) => [{ x: to.x, y: from.y }]);
    for (const id of ["dqPoM03D", "JYkHMAyf", "GzVpoJrp", "ex58zUfm"]) setRoute(id, "Dw5tsBwv", (from, to) => [{ x: to.x, y: from.y }]);
  }
  const allowances = computeVdropAllowances(system);
  system.cables = system.cables.map((cable, index) => ({
    ...cable,
    label: `C${index + 1}`,
    // Pin the catalog/default allowances so the public schedule and editor
    // agree even when the visitor has a different global drop preference.
    vdropClass: allowances.byCable.get(cable.id) === VDROP_PCT_NON_CRITICAL ? "non-critical" : "critical",
    waypoints: routes.get(cable.id) ?? cable.waypoints,
  }));
  system.name = focused ? "Boat example — engine, starter and house banks" : "Boat demo — solar, cabin and stern";
  return system;
}

let focusedDemo: System | undefined;
let fullDemo: System | undefined;

export function buildBoatStarterHouseSystem(): System {
  focusedDemo ??= demoSystem(true);
  return structuredClone(focusedDemo);
}

export function buildBoatDemoSystem(): System {
  fullDemo ??= demoSystem(false);
  return structuredClone(fullDemo);
}
